/**
 * Form pipeline step 3: React Query hook for form submissions
 */

import type { FormSchema, GeneratedFile, GeneratorOptions } from '@core-engine/schema/types.js'
import { createGeneratedFile } from '@core-engine/utils/generated-files.js'
import { toCamelCase, toKebabCase, toPascalCase } from '@core-engine/utils/string.js'
import { resolveSchemaActionImportPath } from '@next-adapter/config/paths.js'

interface StepResult {
  files: GeneratedFile[]
}

export function generateFormHook(
  schema: FormSchema,
  hooksDir: string,
  options: GeneratorOptions
): StepResult {
  const actionImportPath = resolveSchemaActionImportPath(schema.name, options.outputNamespace)
  const formName = schema.name
  const pascal = toPascalCase(formName)
  const camel = toCamelCase(formName)
  const kebab = toKebabCase(formName)
  const successMsg = (schema.successMessage || 'Form submitted successfully').replace(/'/g, "\\'")
  const hasExport = schema.actions?.export === true

  const submissionActionImports = [
    `create${pascal}Submission,`,
    `delete${pascal}Submission,`,
    hasExport ? `export${pascal}SubmissionsCSV,` : null,
    `get${pascal}Submission,`,
    `get${pascal}Submissions,`
  ]
    .filter((line): line is string => line !== null)
    .map((line) => `  ${line}`)
    .join('\n')

  const content = `import {
${submissionActionImports}
} from '@admin/actions/${actionImportPath}'
import type {
  Create${pascal}SubmissionInput,
  Get${pascal}SubmissionsFilters
} from '@admin/actions/${actionImportPath}'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

export function use${pascal}Submissions(
  search?: string,
  submittedAtFrom?: string,
  submittedAtTo?: string,
  page?: number,
  pageSize?: number,
  orderBy?: string,
  orderDirection?: 'asc' | 'desc'
) {
  return useQuery({
    queryKey: [
      '${camel}-submissions',
      search ?? '',
      submittedAtFrom ?? '',
      submittedAtTo ?? '',
      page ?? 0,
      pageSize ?? 20,
      orderBy ?? '',
      orderDirection ?? ''
    ],
    queryFn: () => {
      const filters: Get${pascal}SubmissionsFilters = {}

      if (search) filters.search = search
      if (submittedAtFrom) filters.submittedAtFrom = submittedAtFrom
      if (submittedAtTo) filters.submittedAtTo = submittedAtTo
      if (pageSize != null && pageSize !== -1) {
        filters.limit = pageSize
        filters.offset = (page ?? 0) * pageSize
      }
      if (orderBy) filters.orderBy = orderBy
      if (orderDirection) filters.orderDirection = orderDirection

      return get${pascal}Submissions(Object.keys(filters).length > 0 ? filters : undefined)
    }
  })
}

export function use${pascal}Submission(id: string | null) {
  return useQuery({
    queryKey: ['${camel}-submission', id],
    queryFn: () => {
      if (!id) {
        throw new Error('Submission ID is required')
      }
      return get${pascal}Submission(id)
    },
    enabled: !!id
  })
}

export function useCreate${pascal}Submission() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: Create${pascal}SubmissionInput) => create${pascal}Submission(data),
    onSuccess: async (result) => {
      if (result.success) {
        toast.success('${successMsg}')
        await queryClient.refetchQueries({ queryKey: ['${camel}-submissions'] })
      } else {
        toast.error(result.error || 'Failed to submit form')
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to submit form')
    }
  })
}

export function useDelete${pascal}Submission() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => delete${pascal}Submission(id),
    onSuccess: async () => {
      toast.success('Submission deleted successfully')
      await queryClient.refetchQueries({ queryKey: ['${camel}-submissions'] })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete submission')
    }
  })
}
${
  hasExport
    ? `
export function useExport${pascal}SubmissionsCSV() {
  return useMutation({
    mutationFn: export${pascal}SubmissionsCSV,
    onSuccess: (csvContent) => {
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = \`${formName}-submissions-\${new Date().toISOString().split('T')[0]}.csv\`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success('CSV exported successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to export CSV')
    }
  })
}
`
    : ''
}`

  return {
    files: [createGeneratedFile(`${hooksDir}/use-${kebab}-form.ts`, content)]
  }
}
